import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/nova/components';
import { PLAYABLE_PREVIEW } from '@/nova/theme/tokens';

import { EMPTY_DEMO_STACK, demoLayerShown, demoSettleDelay, demoStackReducer } from '../../card-demo-layers';
import {
  asPreviewMessage,
  postPreviewControl,
  type PreviewAction,
  type PreviewDocState,
} from '../../playable-preview';
import * as S from './PlayableComposer.styles';

export interface PreviewFrameProps {
  /** The single-file preview document (built in the browser from the runtime, or from the backend's preview route). */
  html: string;
  /** The format's CSS pixel size, the size the document is laid out at. */
  w: number;
  h: number;
  /** Shrink factor for the on-screen box (the document keeps its real size inside). Default 1. */
  scale?: number;
  /** Translated accessible name of the frame. */
  title: string;
  /** The owner plays it: a Replay control, and a note naming where a CTA or T&Cs press would go. */
  interactive?: boolean;
  /** A change mounts the document afresh (a Replay from outside), cross-faded in like an edit. */
  run?: number;
  /** A document finished loading. */
  onLoad?: () => void;
  /** A controlled preview document (`preview: true`) reported its state; `fresh` = the first report of a document
   *  just mounted (an edit or a fresh mount of the same document), which starts resting. */
  onPreviewState?: (state: PreviewDocState, fresh: boolean) => void;
}

/** Drives the frame's documents (a controlled preview; any other document ignores it). */
export interface PreviewFrameHandle {
  post: (action: PreviewAction) => void;
}

/** The frame size of a mini-game format: its own `WxH`, or a phone portrait for the responsive one. Pure. */
export const formatFrameSize = (format: string): { w: number; h: number } => {
  const m = /^(\d+)x(\d+)$/.exec(format);
  if (m) return { w: Number(m[1]), h: Number(m[2]) };
  return { w: 360, h: 640 };
};

/** The scale that fits a `w` x `h` frame into a `maxW` x `maxH` box, never enlarging. Pure. */
export const fitScale = (w: number, h: number, maxW: number, maxH: number): number =>
  Math.min(1, maxW / Math.max(1, w), maxH / Math.max(1, h));

/** A press the preview reported, tied to the document (the frame layer) it happened in. */
interface PressNote {
  kind: 'cta' | 'open';
  url: string;
  layer: number;
}

/**
 * A mini-game preview in an iframe sandboxed to scripts only. No `allow-same-origin`, so the document runs in an
 * opaque origin with no access to the Studio (its storage, cookies or DOM), and no `allow-popups` /
 * `allow-top-navigation`, so its CTA cannot leave the frame: the document reports presses with `postMessage`
 * instead, and an interactive frame names where the live ad would go. The document is laid out at the format's
 * real size and scaled into the box.
 *
 * Nothing ever blanks: a new document (an edit, a size switch, Replay) loads hidden over the one on screen and
 * fades in once it has loaded; only then is the old one dropped.
 */
export const PreviewFrame = React.forwardRef<PreviewFrameHandle, PreviewFrameProps>(function PreviewFrame(
  { html, w, h, scale = 1, title, interactive = false, run = 0, onLoad, onPreviewState },
  ref,
): React.ReactElement {
  const { t } = useTranslation();
  const [stack, dispatch] = React.useReducer(demoStackReducer, EMPTY_DEMO_STACK);
  const [replays, setReplays] = React.useState(0);
  const [press, setPress] = React.useState<PressNote | null>(null);
  const frames = React.useRef(new Map<number, HTMLIFrameElement>());
  // The layers whose document already reported a state (the next report of any other is a fresh document's).
  const reported = React.useRef(new Set<number>());
  const latest = React.useRef({ onPreviewState, interactive });
  latest.current = { onPreviewState, interactive };

  // A fresh document for each new html, size or replay (hidden until it loads, over the one on screen).
  React.useEffect(() => {
    dispatch({ type: 'mount', mode: 'idle', html, size: { w, h } });
  }, [html, w, h, run, replays]);

  // Drops each covered frame once its fade has finished (a timer, an external clock).
  React.useEffect(() => {
    const now = Date.now();
    const delay = demoSettleDelay(stack, now, PLAYABLE_PREVIEW.SWAP_MS);
    if (delay === null) return undefined;
    const due = now + delay;
    const timer = window.setTimeout(
      () => dispatch({ type: 'settle', at: Math.max(Date.now(), due), fadeMs: PLAYABLE_PREVIEW.SWAP_MS }),
      delay,
    );
    return () => window.clearTimeout(timer);
  }, [stack]);

  // Forgets the reports of dropped documents.
  React.useEffect(() => {
    const keys = new Set(stack.layers.map((l) => l.key));
    reported.current.forEach((key) => {
      if (!keys.has(key)) reported.current.delete(key);
    });
  }, [stack]);

  // Listens to this frame's own documents (an external message source); other frames' messages are ignored.
  React.useEffect(() => {
    const onMessage = (e: MessageEvent): void => {
      let from: number | null = null;
      for (const [key, el] of frames.current) {
        if (el.contentWindow && e.source === el.contentWindow) from = key;
      }
      if (from === null) return;
      const msg = asPreviewMessage(e.data);
      if (!msg) return;
      if (msg.kind === 'state') {
        const fresh = !reported.current.has(from);
        reported.current.add(from);
        latest.current.onPreviewState?.(msg.state, fresh);
      } else if ((msg.kind === 'cta' || msg.kind === 'open') && latest.current.interactive) {
        setPress({ kind: msg.kind, url: msg.url, layer: from });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  React.useImperativeHandle(
    ref,
    () => ({
      post: (action: PreviewAction) => frames.current.forEach((el) => postPreviewControl(el.contentWindow, action)),
    }),
    [],
  );

  // The document the owner sees: the newest loaded one (the newest while the first loads).
  const shown = stack.layers.filter(demoLayerShown);
  const primary = (shown[shown.length - 1] ?? stack.layers[stack.layers.length - 1])?.key;
  const newest = stack.layers[stack.layers.length - 1]?.key;
  // A note belongs to the document it was pressed in: an edit or a replay hides it.
  const note = press && press.layer === newest ? press : null;

  const box = (
    <S.PreviewBox $w={w * scale} $h={h * scale}>
      {stack.layers.map((layer) => (
        <S.FrameLayer key={layer.key} $shown={demoLayerShown(layer)} aria-hidden={layer.key !== primary || undefined}>
          <S.PreviewIframe
            ref={(el: HTMLIFrameElement | null) => {
              if (el) frames.current.set(layer.key, el);
              else frames.current.delete(layer.key);
            }}
            sandbox="allow-scripts"
            srcDoc={layer.html}
            width={layer.size?.w ?? w}
            height={layer.size?.h ?? h}
            title={layer.key === primary ? title : undefined}
            tabIndex={layer.key === primary ? undefined : -1}
            referrerPolicy="no-referrer"
            onLoad={() => {
              dispatch({ type: 'loaded', key: layer.key, at: Date.now() });
              onLoad?.();
            }}
            $scale={scale}
          />
        </S.FrameLayer>
      ))}
    </S.PreviewBox>
  );
  if (!interactive) return box;
  return (
    <S.PreviewFrameWrap>
      {box}
      <S.PreviewControls>
        <Button variant="text" icon="refresh" label={t('Replay')} onClick={() => setReplays((n) => n + 1)} />
        {note?.kind === 'cta' ? (
          <S.PreviewEventNote text="CTA pressed: in the live ad it opens {{url}}" placeholders={{ url: note.url || '/' }} />
        ) : note?.kind === 'open' ? (
          <S.PreviewEventNote text="T&Cs link pressed: in the live ad it opens {{url}}" placeholders={{ url: note.url }} />
        ) : null}
      </S.PreviewControls>
    </S.PreviewFrameWrap>
  );
});

export default PreviewFrame;
